import { useCustom, useTranslate } from "@refinedev/core";
import { FormCard, SectionHeader, BuildingIcon } from "@fawkes/ui";

import { LoadingState } from "../../components/states";
import { FosterError } from "./FosterError";
import type { Page } from "../../shell/nav";
import type { PortalContact, PortalMe } from "./types";

// jmix-8.5: Mijn gegevens — the foster's profile self-view (legacy MijnGegevensView). Read-only: the
// e-mail + the linked organization names. The `/me` pattern — identity comes from the token, NEVER a
// client-supplied id (PortalController.me resolves foster_identity_id server-side). Organization NAMES are
// federated, so we join `/me` (email + linked organization uuids) with `/contact` (names per organization); an
// unreachable organization degrades to the generic "Opvanglocatie" label, never an error.
export function MijnGegevens({ onNavigate }: { onNavigate?: (page: Page) => void } = {}) {
  const t = useTranslate();

  const me = useCustom<PortalMe>({ url: "portal/me", method: "get" });
  const contact = useCustom<PortalContact>({ url: "portal/contact", method: "get" });

  if (me.isLoading) {
    return <LoadingState />;
  }
  if (me.isError || !me.data?.data) {
    return <FosterError onRetry={() => void me.refetch()} onNavigate={onNavigate} />;
  }

  const profile = me.data.data;
  // Organization name by uuid (best-effort; a federation outage just yields the fallback label).
  const nameByUuid = new Map(
    (contact.data?.data.organizations ?? []).map((s) => [s.organizationUuid, s.name]),
  );

  return (
    <div className="fawkes-portal">
      <FormCard>
        <SectionHeader>{t("portal.profile.emailLabel")}</SectionHeader>
        <p className="fawkes-portal__value">{profile.email ?? "—"}</p>
      </FormCard>

      <FormCard>
        <SectionHeader>{t("portal.profile.organizationsTitle")}</SectionHeader>
        {profile.organizations.length === 0 ? (
          <p className="fawkes-portal__muted">{t("portal.profile.organizationsEmpty")}</p>
        ) : (
          <ul className="fawkes-portal__list">
            {profile.organizations.map((link) => (
              <li key={link.linkId} className="fawkes-portal__row">
                <BuildingIcon width={16} height={16} />
                <span>
                  {nameByUuid.get(link.organizationUuid) || t("portal.profile.organizationFallback")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </FormCard>
    </div>
  );
}
